import { formatMinutesLabel } from './shiftSchedule';

/** Shift policy minutes (min work, break) for attendance list cells. */
export function formatShiftPolicyMinutes(minutes) {
  if (minutes === null || minutes === undefined) {
    return '—';
  }
  return formatMinutesLabel(minutes);
}

/**
 * Format attendance minutes for list display.
 * Special shifts: late/early show "-"; other zero minutes show "-".
 */
export function formatAttendanceMinutes(minutes, isSpecialShift = false, field = null) {
  if (isSpecialShift && (field === 'late' || field === 'early')) {
    return '-';
  }
  const value = Number(minutes) || 0;
  if (value > 0) {
    return `${value} min`;
  }
  if (isSpecialShift) {
    return '-';
  }
  return 'No';
}

/** Single list cell: check-in - check-out (duration). */
export function formatCheckInOutDuration(checkIn, checkOut, duration) {
  const inStr = checkIn && String(checkIn).trim() ? String(checkIn).trim() : '-';
  const outStr = checkOut && String(checkOut).trim() ? String(checkOut).trim() : '-';
  const durStr = duration && String(duration).trim() ? String(duration).trim() : '';

  if (inStr === '-' && outStr === '-' && !durStr) {
    return '-';
  }

  let result = `${inStr} - ${outStr}`;
  if (durStr && durStr !== '-') {
    result += ` (${durStr})`;
  }
  return result;
}

export function formatCheckInOutDurationFromRecord(record) {
  const expired = record?.open_shift_expired === true;
  const duration =
    record?.is_stale_open_session === true || expired
      ? ''
      : record?.total_hours_formatted;
  return formatCheckInOutDuration(
    record?.check_in_time_str,
    expired ? null : record?.check_out_time_str,
    duration
  );
}

/**
 * Parse an attendance UTC timestamp. Naive ISO strings (no Z / offset) are UTC,
 * matching backend naive UTC datetimes. Browsers otherwise treat them as local.
 */
export function parseAttendanceUtcMs(value) {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
  const normalized = hasTimezone ? raw : `${raw.replace(' ', 'T')}Z`;
  const ms = new Date(normalized).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function earlyCheckoutRequiredMinutes(checkInInfo) {
  if (!checkInInfo) {
    return null;
  }
  const threshold = checkInInfo.isSpecialShift
    ? Number(checkInInfo.minWorkMinutes)
    : Number(checkInInfo.scheduledMinutes);
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return null;
  }
  return threshold;
}

export function parseLocalCheckInMs(dateValue, timeValue) {
  if (!dateValue || !timeValue) {
    return null;
  }
  const [hours, minutes] = String(timeValue).split(':').map(Number);
  const checkInDateTime = new Date(dateValue);
  if (
    Number.isNaN(checkInDateTime.getTime()) ||
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return null;
  }
  checkInDateTime.setHours(hours, minutes, 0, 0);
  return checkInDateTime.getTime();
}

/**
 * Whether checkout still needs the early-leave reason box.
 * Uses live elapsed time when check-in UTC can be parsed; otherwise the
 * server is_early_checkout flag from /attendance/status or check-in.
 */
export function needsEarlyCheckoutAcknowledgment(checkInInfo, nowMs = Date.now()) {
  if (!checkInInfo) {
    return false;
  }
  const threshold = earlyCheckoutRequiredMinutes(checkInInfo);
  let checkInMs = parseAttendanceUtcMs(checkInInfo.checkInTimeUtc);
  if (checkInMs == null) {
    checkInMs = parseLocalCheckInMs(checkInInfo.date, checkInInfo.time);
  }
  if (checkInMs == null || threshold == null) {
    return checkInInfo.isEarlyCheckout === true;
  }
  const priorMinutes = Number(checkInInfo.priorDayWorkedMinutes) || 0;
  const elapsedMinutes = Math.max(0, Math.floor((nowMs - checkInMs) / 60000));
  return priorMinutes + elapsedMinutes < threshold;
}

/** AllCheckInOut uses "0 min" for non-special zero values. */
export function formatAttendanceMinutesAdmin(minutes, isSpecialShift = false, field = null) {
  if (isSpecialShift && (field === 'late' || field === 'early')) {
    return '-';
  }
  const value = Number(minutes) || 0;
  if (value > 0) {
    return `${value} min`;
  }
  return '-';
}
